/**
 * metaPixel.js — Meta (Facebook) Pixel loader + safe track wrapper.
 *
 * DESIGN PRINCIPLES (locked by product requirements):
 *
 *   1. NO PIXEL CODE RUNS BEFORE CONSENT.
 *      The base `fbevents.js` script is injected ONLY after `setConsent("accepted")`.
 *      If the user clicks "Odrzucam" (or never chose), `window.fbq` never exists
 *      and every `mpTrack()` call is a no-op.
 *
 *   2. WHITELIST PARAMS.
 *      `mpTrack` accepts ONLY a small set of param keys: value, currency,
 *      content_ids, content_name, content_type, num_items. Anything else is
 *      dropped before send. This is the enforcement of the product ban on
 *      sending scene text, Kodeks Potrzeb / matryca history, CRM notes,
 *      private names, etc. — callers physically cannot pass such data through.
 *
 *   3. NO Advanced Matching, NO Conversions API.
 *      We NEVER call `fbq('init', ID, userData)` with an email/phone/name.
 *      We only fire standard events; server-side CAPI is explicitly forbidden.
 */

export const META_PIXEL_ID = "2451314412046060";
const CONSENT_KEY = "leo_meta_consent";      // "accepted" | "rejected" | null

// Only these param keys are ever passed to fbq. Anything else is silently dropped.
const ALLOWED_PARAM_KEYS = new Set([
  "value",
  "currency",
  "content_ids",
  "content_name",
  "content_type",
  "num_items",
]);

let pixelLoaded = false;

export function getConsent() {
  try { return localStorage.getItem(CONSENT_KEY); } catch { return null; }
}

export function setConsent(value) {
  try { localStorage.setItem(CONSENT_KEY, value); } catch {}
}

/**
 * Inject the FB pixel base script and call fbq('init'). Idempotent — subsequent
 * calls do nothing. Never called before the user clicks "Akceptuję".
 */
export function loadPixel() {
  if (pixelLoaded) return;
  if (getConsent() !== "accepted") return;
  if (typeof window === "undefined") return;

  // Standard Meta pixel bootstrap (verbatim), then init with our pixel id.
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */

  window.fbq("init", META_PIXEL_ID);
  pixelLoaded = true;
}

/**
 * Safe wrapper. If pixel is not loaded (no consent, or not initialized yet)
 * this is a no-op — never throws. Params are filtered against ALLOWED_PARAM_KEYS
 * before hitting fbq.
 */
export function mpTrack(eventName, params) {
  if (!eventName) return;
  if (getConsent() !== "accepted") return;
  if (typeof window === "undefined" || !window.fbq) return;
  const safeParams = {};
  if (params && typeof params === "object") {
    for (const k of Object.keys(params)) {
      if (ALLOWED_PARAM_KEYS.has(k) && params[k] != null && params[k] !== "") {
        safeParams[k] = params[k];
      }
    }
  }
  try {
    if (Object.keys(safeParams).length > 0) {
      window.fbq("track", eventName, safeParams);
    } else {
      window.fbq("track", eventName);
    }
  } catch {
    // never let analytics crash the app
  }
}
